import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight, mdiPlus } from "@mdi/js";
import { Button, Switch } from "@heroui/react";
import { ROUTES, discountDetailPath } from "@/constants/routes";
import {
  discountsStore,
  type DiscountListFilter,
} from "@/stores/DiscountsStore";
import type { DiscountResponseDto } from "@pro-sto/api-client";
import {
  formatDiscountPeriod,
  formatDiscountType,
  formatDiscountValue,
} from "./discount-format";
import "./DiscountsPage.css";

const FILTERS: Array<{ id: DiscountListFilter; label: string }> = [
  { id: "all", label: "Все" },
  { id: "active", label: "Активные" },
  { id: "promocode", label: "Промокод" },
  { id: "manual", label: "Клиентам" },
];

const DiscountsPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const store = discountsStore;

  useEffect(() => {
    void store.loadDiscounts();
  }, []);

  const openDetail = (row: DiscountResponseDto) => {
    navigate(discountDetailPath(row.uuid));
  };

  const meta = store.meta;
  const totalPages = meta?.totalPages ?? 1;

  return (
    <div className="dc-page" data-testid="discounts-page">
      <div className="dc-page__header">
        <h1 className="dc-page__title">Скидки и акции</h1>
        <Button
          variant="primary"
          onPress={() => navigate(ROUTES.DISCOUNT_NEW)}
        >
          <Icon path={mdiPlus} size={0.75} />
          Создать
        </Button>
      </div>

      <div className="dc-filters" role="tablist" aria-label="Фильтр скидок">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={
              store.listFilter === f.id
                ? "dc-filter-chip dc-filter-chip_active"
                : "dc-filter-chip"
            }
            onClick={() => store.setListFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="dc-table-wrap" aria-busy={store.isLoading}>
        {store.discounts.length === 0 && !store.isLoading ? (
          <div className="dc-empty">Скидок пока нет</div>
        ) : (
          <table className="dc-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Значение</th>
                <th>Активна</th>
                <th>Срок</th>
              </tr>
            </thead>
            <tbody>
              {store.discounts.map((row) => (
                <tr
                  key={row.uuid}
                  className="dc-table__row"
                  onClick={() => openDetail(row)}
                >
                  <td>{row.title}</td>
                  <td>{formatDiscountType(row.type)}</td>
                  <td>{formatDiscountValue(row)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Switch
                      isSelected={row.is_active}
                      isDisabled={store.togglingUuid === row.uuid}
                      onChange={(selected) =>
                        void store.toggleActive(row, selected)
                      }
                    >
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch>
                  </td>
                  <td>{formatDiscountPeriod(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 ? (
        <div className="dc-pagination">
          <Button
            isIconOnly
            variant="tertiary"
            isDisabled={store.page <= 1 || store.isLoading}
            aria-label="Предыдущая страница"
            onPress={() => store.setPage(store.page - 1)}
          >
            <Icon path={mdiChevronLeft} size={0.85} />
          </Button>
          <span>
            {store.page} / {totalPages}
          </span>
          <Button
            isIconOnly
            variant="tertiary"
            isDisabled={store.page >= totalPages || store.isLoading}
            aria-label="Следующая страница"
            onPress={() => store.setPage(store.page + 1)}
          >
            <Icon path={mdiChevronRight} size={0.85} />
          </Button>
        </div>
      ) : null}
    </div>
  );
});

DiscountsPage.displayName = "DiscountsPage";
export default DiscountsPage;
